import styled from '@emotion/styled';
import * as Dialog from '@radix-ui/react-dialog';
import { keyframes } from '@emotion/react';
import { COLORS } from '../header/header.styled';

/* ===== Desktop sidebar panels ===== */

export const DesktopSidePanel = styled.div<{ collapsed?: boolean }>`
  width: ${({ collapsed }) => (collapsed ? '56px' : '300px')};
  flex-shrink: 0;
  height: 100%;
  background: ${COLORS.surface};
  border-right: 1px solid ${COLORS.sidebarBorder};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: width 240ms cubic-bezier(0.22, 0.61, 0.36, 1);

  img {
    display: block;
    max-width: 100%;
    height: auto;
    user-select: none;
    -webkit-user-drag: none;
  }
`;

export const CollapseToggle = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${COLORS.textSecondary};
  border-radius: 8px;
  transition: background 150ms ease;

  &:hover {
    background: #2b2d33;
  }

  &:active,
  &[data-pressed='true'] {
    background: #23252a;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const NavLink = styled.button<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  border: none;
  background: transparent;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  padding: 10px 12px;
  border-radius: 8px;
  font-family: Inter, system-ui, sans-serif;
  font-weight: 500;
  font-size: 15px;
  line-height: 140%;
  color: ${({ disabled }) => (disabled ? '#4a4c54' : '#5E84ED')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  text-align: left;
  transition: opacity 150ms ease, background 150ms ease;

  &:hover {
    opacity: ${({ disabled }) => (disabled ? 0.5 : 0.8)};
  }

  &:active,
  &[data-pressed='true'] {
    background: #23252a;
  }
`;

/* ===== Overlay (fade) ===== */
const overlayIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const overlayOut = keyframes`
  from { opacity: 1; }
  to   { opacity: 0; }
`;

export const Overlay = styled(Dialog.Overlay)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 1000;

  &[data-state='open'] {
    pointer-events: auto;
    animation: ${overlayIn} 220ms ease;
    animation-fill-mode: forwards;
  }

  &[data-state='closed'] {
    pointer-events: none;
    animation: ${overlayOut} 200ms ease;
    animation-fill-mode: forwards;
  }
`;

/* ===== Drawer (slide) ===== */
const slideInLeft = keyframes`
  from { transform: translateX(-100%); }
  to   { transform: translateX(0); }
`;

const slideOutLeft = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(-100%); }
`;

export const Side = styled(Dialog.Content)`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: 320px;
  box-sizing: border-box;
  padding-top: env(safe-area-inset-top, 0px);
  background: ${COLORS.surface};
  color: ${COLORS.textPrimary};
  border-right: 1px solid ${COLORS.sidebarBorder};
  box-shadow: 2px 0 18px rgba(0, 0, 0, 0.35);
  display: flex;
  flex-direction: column;
  will-change: transform;
  z-index: 1001;
  border-top-right-radius: 16px;
  border-bottom-right-radius: 16px;

  &[data-state='open'] {
    pointer-events: auto;
    animation: ${slideInLeft} 240ms cubic-bezier(0.22, 0.61, 0.36, 1) forwards;
  }

  &[data-state='closed'] {
    pointer-events: none;
    animation: ${slideOutLeft} 220ms cubic-bezier(0.22, 0.61, 0.36, 1) forwards;
  }

  *:focus,
  *:focus-visible {
    outline: none;
    box-shadow: none;
  }

  img {
    display: block;
    max-width: 100%;
    height: auto;
    user-select: none;
    -webkit-user-drag: none;
  }
`;

export const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0px 16px;
`;

export const IconBtn = styled.button`
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
  border: none;
  margin: 0;
  line-height: 0;
  cursor: pointer;
  border-radius: 8px;
  transition: background 150ms ease;

  &:active,
  &[data-pressed='true'] {
    background: #23252a;
  }
`;

export const Body = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 0 16px;
  overflow: hidden;
  flex: 1;
  min-height: 0;
`;

export const MainContent = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  margin-top: 14px;
  display: flex;
  flex-direction: column;
`;

export const SearchBarRow = styled.div`
  width: 100%;
`;

export const GoToHome = styled.button`
  color: #5E84ED;
  font-size: 15px;
  font-family: Inter;
  font-weight: 600;
  line-height: 19.60px;
  word-wrap: break-word;
  background: transparent;
  border: none;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  transition: background 150ms ease;

  &:active,
  &[data-pressed='true'] {
    background: #23252a;
  }
`;

/* ===== My Notes section ===== */

export const NotesSection = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  min-height: 0;
`;

export const NotesHeaderRow = styled.div`
  height: 56px;
  display: flex;
  align-items: center;
`;

export const NotesLabel = styled.div`
  display: flex;
  align-items: center;

  font-family: Inter, system-ui, sans-serif;
  font-weight: 400;
  font-style: normal;
  font-size: 14px;
  line-height: 140%;
  letter-spacing: 0;
  text-align: center;
  vertical-align: middle;
  color: #c1c4cc;
`;

export const AddNoteButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 10px 12px;
  border-radius: 8px;
  transition: background 150ms ease;

  display: inline-flex;
  align-items: center;
  justify-content: flex-start;

  &:active,
  &[data-pressed='true'] {
    background: #23252a;
  }
`;

export const AddNoteText = styled.span`
  font-family: Inter, system-ui, sans-serif;
  font-weight: 500;
  font-style: normal;
  font-size: 14px;
  line-height: 140%;
  letter-spacing: 0;
  text-align: center;
  vertical-align: middle;
  color: #5e84ed;
`;

/* ===== Notes list container (scrolling handled by ScrollArea wrapper) ===== */
export const NotesListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const NoteItem = styled.button`
  border: 0;
  background: transparent;
  padding: 8px 12px;
  border-radius: 8px;
  text-align: left;
  cursor: pointer;
  transition: background 150ms ease;

  &:active,
  &[data-pressed='true'] {
    background: #23252a;
  }
`;

export const NoteRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
`;

export const NoteTitle = styled.div`
  color: #e3e7f3;
  font-size: 14px;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 210px;
`;

export const NoteDate = styled.div`
  color: #7d8496;
  font-size: 12px;
  margin-left: auto;
`;

export const LoadMoreButton = styled.button`
  border: none;
  background: #2b2d33;
  cursor: pointer;
  padding: 8px 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;

  font-family: Inter, system-ui, sans-serif;
  font-weight: 500;
  font-size: 13px;
  line-height: 140%;
  color: #b7bdcb;
  transition: background 150ms ease;

  &:hover {
    background: #35373f;
  }

  &:active,
  &[data-pressed='true'] {
    background: #23252a;
    transform: scale(0.97);
    opacity: 0.8;
  }
`;

export const SpinnerWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 0;
`;

/* Animated search results sheet */
export const AnimatedSheet = styled.div<{ active: boolean; top: number }>`
  position: absolute;
  left: 0;
  right: 0;
  top: ${({ top }) => `${top}px`};
  bottom: 0;

  display: flex;
  flex-direction: column;
  overflow: auto;
  padding-bottom: var(--keyboard-height-final, 0px);

  border-top: 1px solid #2b2d33;
  background: ${COLORS.surface};
  transform: translateY(${({ active }) => (active ? '0' : '-8px')});
  opacity: ${({ active }) => (active ? 1 : 0)};
  pointer-events: ${({ active }) => (active ? 'auto' : 'none')};
  transition:
    transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1),
    opacity 180ms linear;
  overflow: hidden; /* Safari fallback for contain: paint */
  contain: paint;
`;

/* ===== Static styles for bottom area (legacy; motion handled elsewhere) ===== */
export const BottomAreaShell = styled.div`
  display: flex;
  flex-direction: column-reverse; /* panel above, row below */
  border-top: 1px solid #2b2d33;
  padding: 0;
  background: ${COLORS.surface};
  padding-bottom: calc(var(--keyboard-height-final, 0px) + env(safe-area-inset-bottom, 0px) + 16px);
`;

export const ProfileRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  padding: 8px 12px;
  border-top: 1px solid #2b2d33;
  border-radius: 8px;
  transition: background 150ms ease;

  &:active,
  &[data-pressed='true'] {
    background: #23252a;
  }
`;

export const ProfileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const Avatar = styled.div<{ src?: string }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${({ src }) => (src ? `url(${src}) center/cover` : '#444')};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 8px;
`;

export const RowButton = styled.button`
  display: flex;
  font-family:
    Inter,
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    sans-serif;
  font-weight: 500;
  font-style: normal;
  font-size: 15px;
  line-height: 140%;
  letter-spacing: 0;
  text-align: left;
  vertical-align: middle;
  height: 44px;
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
`;

export const LogoutRow = styled(RowButton)`
  color: #f66;
`;

export const Label = styled.span`
  flex: 1;
  color: #ffffff;
`;

export const DisplayName = styled.div`
  font-family:
    Inter,
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    sans-serif;
  font-weight: 500;
  font-style: normal;
  font-size: 14px;
  line-height: 120%;
  letter-spacing: 0;
  text-align: center;
  vertical-align: middle;
  color: #ffffff;
`;

export const SafeArea = styled.div`
  height: env(safe-area-inset-bottom, 0px);
  width: 100%;
  background: transparent;
  flex-shrink: 0;
`;

/* ===== Search results ===== */

export const SearchResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 0;
  overflow: auto;
  flex: 1;
`;

export const SearchSectionLabel = styled.div`
  font-family: Inter, system-ui, sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 140%;
  color: #7d8496;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 12px 12px 4px;

  &:first-of-type {
    padding-top: 4px;
  }
`;

export const SearchResultItem = styled.button`
  display: flex;
  flex-direction: column;
  gap: 3px;
  border: none;
  background: transparent;
  padding: 8px 12px;
  border-radius: 8px;
  text-align: left;
  cursor: pointer;
  transition: background 150ms ease;
  width: 100%;

  &:active,
  &[data-pressed='true'] {
    background: #23252a;
  }
`;

export const SearchResultTitle = styled.div`
  color: #e3e7f3;
  font-family: Inter, system-ui, sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const SearchResultSnippet = styled.div`
  color: #7d8496;
  font-family: Inter, system-ui, sans-serif;
  font-weight: 400;
  font-size: 13px;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const SearchLoadingText = styled.div`
  font-family: Inter, system-ui, sans-serif;
  font-weight: 500;
  font-size: 13px;
  line-height: 140%;
  color: #7d8496;
  text-align: center;
  padding: 24px 0;
`;

export const SearchEmptyText = styled.div`
  font-family: Inter, system-ui, sans-serif;
  font-weight: 400;
  font-size: 13px;
  line-height: 140%;
  color: #7d8496;
  text-align: center;
  padding: 24px 0;
`;

// Fullscreen modal content (replaces side drawer for FilesDrawer)
export const Fullscreen = styled(Dialog.Content)`
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding-bottom: calc(var(--keyboard-height-final, 0px) + env(safe-area-inset-bottom, 0px));
  background: ${COLORS.surface};
  outline: none;
  z-index: 1003;
  overscroll-behavior: contain; /* Prevent iOS overscroll background peek */

  &[data-state='open'] {
    pointer-events: auto;
  }

  &[data-state='closed'] {
    pointer-events: none;
  }
`;
